import { formatKey, KEY_SHORTCUTS } from '@lib/keyboard/shortcuts';
import { formatClockLabel, formatDurationToString } from '@lib/utils/units';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import { ReactNode } from 'react';
import css from './SelectionBar.module.scss';

interface SelectionBarProps {
  /** Absolute media seconds. */
  start: number;
  end: number;
  /**
   * What undoing means here. A moment opened from search was seeded with the result's own
   * bounds, so there is a range to go back TO — clearing it outright would throw away the thing
   * the user opened. Library media started with nothing, so the same control removes the range.
   */
  variant: 'reset' | 'clear';
  onUndo: () => void;
  /**
   * Opens the Comments panel with its composer focused. It needs no range handed to it: the
   * composer adopts the marked range as its anchor on its own (see `draftAnchor`), so this is a
   * way there, not a second place to write. Omitted where the reader cannot comment — a
   * read-only library — which drops the button rather than offering one the API would refuse.
   */
  onComment?: () => void;
}

/**
 * States, in one line under the player, what the marked range actually is.
 *
 * It exists because the range is otherwise only legible as a coloured band a few pixels tall,
 * and because the action that consumes it sits in the modal's header (desktop) or its sticky
 * footer (mobile) — nowhere near the bar. Without this the user drags, the button's behaviour
 * silently changes, and nothing anywhere says so.
 *
 * The wording is deliberately neutral about *what* will happen to the range. In a
 * transcript-only library the accented button is the clip verb, not the Agent
 * (`splitMediaActions` decides, not this component), so a line promising the range goes to an
 * Agent would be wrong on that surface. Naming the outcome is `MediaActionBar`'s job, because
 * it is the thing that knows which control the agent verb ended up on — the pill's tooltip
 * when the verb is the pill, the ⋯ / drawer row's own label when it is not.
 */
export const SelectionBar = ({ start, end, variant, onUndo, onComment }: SelectionBarProps) => (
  <div className={css.row}>
    <span className={css.swatch} />
    <span className={css.label}>Selected</span>
    <span className={css.range}>
      {formatClockLabel(start)} – {formatClockLabel(end)}
    </span>
    <span className={css.duration}>
      {/* Measured between the two labels above, not off the raw floats. `formatClockLabel`
          floors, so a 76.4s range reading "0:00 – 1:16" has to say "1 min 16 sec" — taking the
          float would round it to 1 min 16 sec here and 1 min 17 sec one frame later, and a
          caption whose three numbers disagree reads as a bug.

          Which leaves the range that floors to nothing. The narrowest a drag may commit is
          `MIN_SELECTION_PERCENT` of the WHOLE media (marking is `full`-mode only), so under
          roughly three minutes that is sub-second and both clock labels land on the same
          integer — and `formatDurationToString(0)` would then read "0 sec" over a band the
          viewer can see and handles they can drag. The one line whose job is to say what the
          range is would be the one denying there is one.

          `< 1` rather than `=== 0`, so this also stands in for the `Math.max(0, …)` that used
          to sit here: an inverted range is not reachable (`spanFrom` orders a drag's two ends,
          and only a scene the playhead is inside can be adopted), but if one ever were, the
          floor is what keeps a negative out of the caption. */}
      {Math.floor(end) - Math.floor(start) < 1
        ? 'under 1 sec'
        : formatDurationToString(Math.floor(end) - Math.floor(start), { detail: 2 })}
    </span>
    <div className={css.controls}>
      {/* Before the undo, not after it: the row's last control is its way out, and the one
          that keeps the range should not be the one a reader reaching for the edge lands on. */}
      {onComment && (
        <button
          type="button"
          className={css.comment}
          onClick={onComment}
          aria-label="Comment on the selection"
          aria-keyshortcuts={formatKey(KEY_SHORTCUTS.startComment)}
        >
          <ChatBubbleOutlineRoundedIcon className={css.controlIcon} />
          <span className={css.controlLabel}>Comment</span>
        </button>
      )}
      <button
        type="button"
        className={css.undo}
        onClick={onUndo}
        // The accessible name says which of the two this is; the glyph alone does not.
        aria-label={variant === 'reset' ? 'Reset to the original moment' : 'Clear the selection'}
      >
        {variant === 'reset' ? (
          <RestartAltRoundedIcon className={css.controlIcon} />
        ) : (
          <CloseRoundedIcon className={css.controlIcon} />
        )}
        <span className={css.controlLabel}>{variant === 'reset' ? 'Reset' : 'Clear'}</span>
      </button>
    </div>
  </div>
);

/**
 * Teaches the gesture, in the slot the range readout will occupy.
 *
 * A progress bar does not look draggable — it looks clickable, which it also is — and on a
 * touch device there is no hover to discover the difference with. The scene button in the
 * Scenes panel is the only self-evident way in, and it is only there for media that has
 * scenes.
 *
 * Occupying the same row as `SelectionBar` is the point: marking a range swaps the copy rather
 * than pushing the panel down, so the thing that taught the gesture is also the thing that
 * confirms it worked. It retires itself for good once the user has marked a range anywhere —
 * see `hasMarkedMediaRange` — so there is nothing to dismiss.
 */
export const SelectionHint = () => (
  <div className={css.row}>
    <span className={css.hint}>Drag along the bar to select part of this media</span>
  </div>
);

/**
 * Holds the readout's place under the player, empty or not.
 *
 * Not spacing for its own sake: the player above it is an `aspect-ratio` box with
 * `max-height: 100%`, so in a height-constrained column every row added below it takes width
 * off the video — and off the scrub bar inside it. `SelectionBar` appears on the first frame of
 * the very gesture that is measured against that bar, so without the space already reserved the
 * bar narrowed by tens of pixels mid-drag and the range jumped sideways under the pointer, once
 * per drag, every drag.
 *
 * Only where the player is height-constrained. The mobile layout stacks a full-width player in
 * a `flex-shrink: 0` section, so the row appearing below it moves nothing horizontally, and 32px
 * of permanent blank under the video costs more there than it buys.
 */
export const SelectionSlot = ({ children }: { children: ReactNode }) => (
  <div className={css.slot}>{children}</div>
);
